import i18next from "@/lib/i18n";
import { formatNumber } from "@/lib/format";
import { parseUnassignedPayload } from "@/features/routes/lib/unassignedBreakdown";
import type {
  LocalizedNotification,
  TranslatableNotification,
} from "@/features/notifications/lib/translateNotification";

/**
 * BUG-677: system notifications whose backend title/body are English (or whose
 * one generic `TYPE_MAP` sentence hid the real cause), rendered from the
 * notification's TYPE and `extra_data` in the viewer's language.
 *
 * Owner: «чтобы перевод на русский был, а то часть ошибок системных на англ» and
 * «все ошибки были осознанные и передавали реальную причину». Seen live: «Plan
 * allowance reached — You have used all 1000 stops…» in a Russian interface.
 *
 * Every branch below was written against its emitter in core, not guessed:
 *   - `billing_overflow_started` — tenants/services.py `_notify_monthly_stops_overflow`
 *   - `billing_overdue`          — billing/services.py `send_dunning_reminder`
 *   - `invoice_ready`            — billing/tasks.py `_notify_invoice_document_ready`
 *   - `route_changed` ×5         — planning/tasks.py (batch summary, standalone
 *     optimization, re-optimize eviction, two depot-propagation no-ops)
 *   - `chat_message` photo/location — chat/push.py (`code`, UR-10)
 * Each returns `null` when the row does not match, so the caller keeps its
 * existing path (TYPE_MAP, then the raw backend text).
 */

type ExtraData = Record<string, unknown> | null | undefined;

/** A finite number off `extra_data` (or its `params`), else `null`. */
function readNumber(source: ExtraData, key: string): number | null {
  const value = source?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** A non-empty trimmed string off `extra_data`, else `null`. */
function readString(source: ExtraData, key: string): string | null {
  const value = source?.[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function readParams(extraData: ExtraData): Record<string, unknown> | null {
  const params = extraData?.params;
  return typeof params === "object" && params !== null && !Array.isArray(params)
    ? (params as Record<string, unknown>)
    : null;
}

/**
 * `billing_overflow_started`: the tenant has used its monthly stop allowance.
 *
 * Payload: `code: BILLING_OVERFLOW_STARTED`, `params: {allowance, stop_count,
 * over_by}`. The overflow PRICE is not in the payload (see the BUG-677 report),
 * so the body says where the price comes from instead of inventing a figure.
 */
function translateOverflow(extraData: ExtraData): LocalizedNotification {
  const t = i18next.t.bind(i18next);
  const params = readParams(extraData);
  const allowance = readNumber(params, "allowance");
  const stopCount = readNumber(params, "stop_count");
  const overBy = readNumber(params, "over_by");
  const title = t("notifications.types.billing_overflow_started.title");
  if (allowance === null || stopCount === null) {
    return {
      title,
      body: t("notifications.types.billing_overflow_started.body"),
    };
  }
  return {
    title,
    body: t("notifications.types.billing_overflow_started.bodyFigures", {
      allowance: formatNumber(allowance),
      stopCount: formatNumber(stopCount),
      overBy: formatNumber(overBy ?? Math.max(stopCount - allowance, 0)),
    }),
  };
}

/**
 * `billing_overdue`: a dunning reminder. The payload carries only
 * `invoice_number` (and `billing_record_id`); amount, currency and period are in
 * the English body alone (see the BUG-677 report), so the sentence names the
 * invoice and nothing it cannot prove.
 */
function translateOverdue(extraData: ExtraData): LocalizedNotification {
  const t = i18next.t.bind(i18next);
  const invoice = readString(extraData, "invoice_number");
  return {
    title: t("notifications.types.billing_overdue.title"),
    body: invoice
      ? t("notifications.types.billing_overdue.body", { invoice })
      : t("notifications.types.billing_overdue.bodyNoNumber"),
  };
}

/** `invoice_ready`: the detailed invoice PDF finished rendering. */
function translateInvoiceReady(extraData: ExtraData): LocalizedNotification {
  const t = i18next.t.bind(i18next);
  const recordId = readNumber(extraData, "record_id");
  const invoice =
    readString(extraData, "invoice_number") ??
    (recordId !== null ? `#${recordId}` : null);
  return {
    title: t("notifications.types.invoice_ready.title"),
    body: invoice
      ? t("notifications.types.invoice_ready.body", { invoice })
      : t("notifications.types.invoice_ready.bodyNoNumber"),
  };
}

/** The English sentence `run_optimization` writes for a standalone run. */
const OPTIMIZATION_COMPLETE_RE =
  /^Optimization complete: (\d+) routes?, (\d+) unassigned stops?\./;

/** The two depot-propagation no-op bodies (planning/tasks.py). No code, no extra_data. */
const DEPOT_NOOP_BODIES: Record<string, string> = {
  "No planned routes to recalculate.":
    "notifications.routeChanged.depotNoPlannedRoutes",
  "Depot settings saved. No routes with stops to recalculate.":
    "notifications.routeChanged.depotNoRoutesWithStops",
};

/**
 * `route_changed` is written by five different planning paths, and `TYPE_MAP`
 * renders all of them as «Маршрут {{route}} обновлён» — or, with no route name in
 * the payload, as the raw English body. Each variant is recognized by the facts it
 * carries and told as what actually happened.
 */
function translateRouteChanged(
  extraData: ExtraData,
  rawBody: string | undefined,
): LocalizedNotification | null {
  const t = i18next.t.bind(i18next);

  // 1. Re-optimize evicted orders: an unassigned breakdown + the route id.
  const unassigned = parseUnassignedPayload(extraData);
  const routeId =
    readString(extraData, "route_id") ?? readNumber(extraData, "route_id");
  if (unassigned !== null && routeId !== null) {
    const route = readString(extraData, "route_name") ?? `#${routeId}`;
    return {
      title: t("notifications.routeChanged.ordersEvictedTitle"),
      body: t("notifications.routeChanged.ordersEvictedBody", {
        route,
        count: unassigned.unassigned_total,
      }),
    };
  }

  // 2. A batch recalculation that lost nothing (the failing batch is typed
  //    `optimization_failed` and handled with the failures).
  const batchTotal = readNumber(extraData, "batch_total");
  if (batchTotal !== null && readNumber(extraData, "failed_total") === 0) {
    return {
      title: t("notifications.routeChanged.batchDoneTitle"),
      body: t("notifications.routeChanged.batchDoneBody", {
        routes: formatNumber(
          readNumber(extraData, "succeeded_total") ?? batchTotal,
        ),
      }),
    };
  }

  // 3. A standalone optimization run. The counts live only in the English
  //    sentence (see the BUG-677 report); the skipped-order count is structured.
  const match = rawBody ? OPTIMIZATION_COMPLETE_RE.exec(rawBody) : null;
  const skipped =
    extraData?.code === "ORDER_NOT_GEOCODED"
      ? readNumber(extraData, "ungeocoded_total")
      : null;
  if (match !== null || skipped !== null) {
    const parts: string[] = [];
    if (match !== null) {
      parts.push(
        t("notifications.routeChanged.optimizedCounts", {
          routes: match[1],
          stops: match[2],
        }),
      );
    }
    if (skipped !== null && skipped > 0) {
      parts.push(
        t("notifications.routeChanged.optimizedSkipped", {
          skipped: formatNumber(skipped),
        }),
      );
    }
    return {
      title: t("notifications.routeChanged.optimizedTitle"),
      body: parts.length > 0 ? parts.join(" ") : undefined,
    };
  }

  // 4. Depot settings saved with nothing to recalculate.
  const noopKey = rawBody ? DEPOT_NOOP_BODIES[rawBody] : undefined;
  if (noopKey) {
    return {
      title: t("notifications.routeChanged.depotTitle"),
      body: t(noopKey),
    };
  }

  // 5. An ordinary route edit — TYPE_MAP's «Маршрут {{route}} обновлён».
  return null;
}

/**
 * `optimization_failed` batch summary: no `code`, but the counts are structured
 * (`batch_total`, `failed_total`, `succeeded_total`). Returns `null` for the
 * coded single-run failures, which `translateFailure` words from the code.
 */
export function translateBatchFailure(
  extraData: ExtraData,
): LocalizedNotification | null {
  if (typeof extraData?.code === "string") return null;
  const total = readNumber(extraData, "batch_total");
  const failed = readNumber(extraData, "failed_total");
  if (total === null || failed === null) return null;
  const t = i18next.t.bind(i18next);
  return {
    title: t("notifications.routeChanged.batchFailedTitle"),
    body: t("notifications.routeChanged.batchFailedBody", {
      succeeded: formatNumber(
        readNumber(extraData, "succeeded_total") ?? total - failed,
      ),
      total: formatNumber(total),
      failed: formatNumber(failed),
    }),
  };
}

/**
 * The literal `chat/push.py` writes for an image-only message. Rows stored
 * before UR-10 carry no `code`, so this stays as the fallback for them.
 */
const CHAT_PHOTO_BODY = "📎 Photo";

/**
 * `chat_message` (UR-10): the platform's own placeholders — a photo, a
 * location — are keyed on `extra_data.code` and rendered in the viewer's
 * language; the sender's own text is never translated (no code → `null`).
 *   - `CHAT_MESSAGE_PHOTO`, `params: {}`
 *   - `CHAT_MESSAGE_LOCATION`, `params: {label}` (`""` when not geocoded)
 */
function translateChatMessage(
  extraData: ExtraData,
  rawBody: string | undefined,
): LocalizedNotification | null {
  const title = i18next.t("notifications.types.chat_message.title");
  const code = extraData?.code;
  if (code === "CHAT_MESSAGE_LOCATION") {
    const label = readString(readParams(extraData), "label");
    return {
      title,
      body: label
        ? i18next.t("notifications.types.chat_message.locationWithLabel", {
            label,
          })
        : i18next.t("notifications.types.chat_message.location"),
    };
  }
  if (code === "CHAT_MESSAGE_PHOTO" || rawBody === CHAT_PHOTO_BODY) {
    return {
      title,
      body: i18next.t("notifications.types.chat_message.photo"),
    };
  }
  return null;
}

/**
 * Localizes a system notification from its type + `extra_data`, or returns
 * `null` to leave the row to the caller's existing path.
 */
export function translateSystemNotification(
  notification: TranslatableNotification,
): LocalizedNotification | null {
  const type = notification.notification_type ?? undefined;
  const extraData = notification.extra_data;
  const rawBody = notification.body ? notification.body : undefined;

  switch (type) {
    case "billing_overflow_started":
      return translateOverflow(extraData);
    case "billing_overdue":
      return translateOverdue(extraData);
    case "invoice_ready":
      return translateInvoiceReady(extraData);
    case "route_changed":
      return translateRouteChanged(extraData, rawBody);
    case "chat_message":
      return translateChatMessage(extraData, rawBody);
    default:
      return null;
  }
}
